import { throttle }                   from "lodash";
import { useRouter }                  from "next/router";
import React, { useEffect, useState } from "react";

import { useGetHasVotedAtom, useSetHasVotedAtom } from "_common/hooks/polls";
import { useGetSessionEnded }                     from "_common/hooks/stream";
import Toast                                      from "_common/toast";
import { isPageRoute }                            from "_common/utils";
import { useGetMessage }                          from "_components/features/ivsChat/states";
import {
	useGetGlobalPollsCount,
	useGetPollsOpen,
	useSetGlobalPollsCount,
	useSetPollsEndedData,
	useSetPollsOpen,
	useSetPollsStartedData
} from "_components/pages/studio/states";
import { DEBOUNCE_POLLS_DATA_IN_MS }        from "_config/index";
import { useGetUpdateSession }              from "_jotai/liveSession";
import { getPollsConfigData, getPollsData } from "_services/api/broadcast";

import Presentation from "./presentation";

const component = props => {

	const {
		broadcastId
	} = props;

	const [ createPoll, setCreatePoll ] = useState ( false );
	const [ tab, setTab ]               = useState ( "polls-0" );
	const router                        = useRouter ();
	const polls                         = useGetPollsOpen ();
	const setPolls                      = useSetPollsOpen ();
	const sessionData                   = useGetUpdateSession ();
	const isSessionEnded                = useGetSessionEnded ();
	const [ liveData, setLiveData ]     = useState ( [] );
	const [ endedData, setEndedData ]   = useState ( [] );
	const setPollsEndData               = useSetPollsEndedData ();
	const setPollsStartData             = useSetPollsStartedData ();
	const updateHasVoted                = useSetHasVotedAtom ();
	const pollsCount                    = useGetGlobalPollsCount ();
	const setPollsCount                 = useSetGlobalPollsCount ();
	const hasVotedValue                 = useGetHasVotedAtom ();
	const message                       = useGetMessage ();

	const [ pollsConfig, setPollsConfig ] = useState ( {} );

	const isStudioPage = isPageRoute ( { router, page: "studio" } );
	const isPopOutChat = isPageRoute ( { router, page: "popout" } );

	const togglePolls = () => {
		setPolls ( prev => !prev );
	};

	useEffect ( () => {
		if ( pollsCount )
			setPollsCount ( pollsCount );
	}, [ pollsCount ] );

	useEffect ( () => {
		if ( broadcastId && sessionData && sessionData?.pollsEnabled && !isSessionEnded ) {
			initPolls ();
		}

	}, [ sessionData?.pollsEnabled, isSessionEnded, broadcastId ] );

	const handleChangeTab = event => {
		setTab ( event );
	};

	const segregateData = pollsData => {
		if ( !pollsData ) return;
		const updatedLiveData  = pollsData.filter ( poll => poll.isActive === true ).map ( ( { hasVoted, ...keepAttrs } ) => keepAttrs );
		const updatedEndedData = pollsData.filter ( poll => poll.isActive === false ).map ( ( { hasVoted, ...keepAttrs } ) => keepAttrs );

		setLiveData ( updatedLiveData );
		setEndedData ( updatedEndedData );

	};
	const getInitialPollsData = async broadcastId => {
		try {
			const data      = await getPollsData ( broadcastId );
			const pollsData = data?.polls;
			const pollCount = pollsData.filter ( function ( item ) {
				const { hasVoted, isActive } = item;

				if ( isActive === true && !hasVoted ) {
					return true;
				} else {
					return false;
				}
			} ).length;

			setPollsCount ( pollCount );
			if ( pollsData ) {
				segregateData ( pollsData );
				updateHasVotedFlag ( pollsData, "API" );
			}

		} catch ( e ) {
			// err handling if first response fails
		}

	};
	const initPolls = async () => {
		getInitialPollsData ( broadcastId );
		const configData = await getPollsConfigData ();

		setPollsConfig ( configData );
	};

	const updateDataOnVote = data => {

		const { pollId, votes: newVotes } = data;

		const dataWithIdIndex = liveData.findIndex ( s => s.id === pollId );
		const dataWithId      = liveData?.[dataWithIdIndex];
		const copyLiveData    = [ ...liveData ];

		const newOptions = dataWithId?.options?.map ( opt => {
			let { id, title, votes } = opt;

			if ( newVotes[id] ) {
				votes = newVotes[id];
			}

			return {
				id,
				title,
				votes
			};
		} );

		const newObj = {
			id        : dataWithId?.id,
			title     : dataWithId?.title,
			options   : newOptions,
			isActive  : dataWithId?.isActive,
			createdAt : dataWithId?.createdAt,
			creator   : dataWithId?.creator
		};

		copyLiveData.splice ( dataWithIdIndex, 1, newObj );

		return setLiveData ( copyLiveData );
	};

	const updateDataOnVoteThrottle = throttle ( updateDataOnVote, DEBOUNCE_POLLS_DATA_IN_MS );

	useEffect ( () => {
		if ( !message || !sessionData?.ivsChatEnabled )
			return;

		ivsMessages ( message );
	}, [ message, sessionData?.ivsChatEnabled ] );

	const ivsMessages = message => {
		const eventName = message?.attributes?.eventName;
		let commentText = message?.content;

		if ( commentText === "NO_CONTENT" ) {
			commentText = "";
		}

		let metadata = null;

		try {
			if ( message?.attributes?.metadata && message.attributes.metadata.trim () !== "" ) {
				metadata = JSON.parse ( message.attributes.metadata );
			}
		} catch ( error ) {
			return;
		}

		if ( !metadata )
			return;

		const __data = metadata;

		if ( eventName === "CREATE_POLL" ) {
			if ( __data ) {
				setPollsStartData ( __data );
				setPollsCount ( count => count + 1 );
				setLiveData ( data => [ __data, ...data ] );
				updateHasVotedFlag ( __data, "CREATE" );
			}

		}

		if ( eventName === "VOTE_UPDATE" ) {

			if ( __data ) {
				updateDataOnVoteThrottle ( __data );
			}

		}

		if ( eventName === "POLL_END" ) {
			if ( __data?.pollId ) {
				updateDataOnEnd ( __data?.pollId );
			}
			if ( __data ) {
				setPollsEndData ( __data );
			}

		}

		if ( eventName === "POLL_DELETE" ) {

			if ( __data?.pollId ) {
				updateDataOnDelete ( __data?.pollId );
				updateHasVotedFlag ( __data?.pollId, "DELETE" );
			}

		}

	};

	const toggleCreatePoll = disableCreatePoll => {
		if ( disableCreatePoll ) {
			return Toast.error ( "Only 3 Polls can be live at a time." );
		}

		setCreatePoll ( prev => !prev );
	};

	const updateDataOnEnd = pollId => {

		if ( !hasVotedValue?.[pollId] ) {
			setPollsCount ( count => count - 1 );
		}

		const ObjWithId     = liveData.find ( s => s.id === pollId );
		const dataWithoutId = liveData.filter ( s => s.id !== pollId );

		const { id, title, options, createdAt, creator } = ObjWithId || {};
		const updatedObj                                 = {
			id,
			title,
			options,
			isActive: false,
			createdAt,
			creator
		};

		setLiveData ( dataWithoutId );
		setEndedData ( data => [ updatedObj, ...data ] );
	};

	const updateDataOnDelete = pollId => {
		const ObjWithIdLive = liveData?.find ( s => s.id === pollId );
		const ObjWithIdEnd  = endedData?.find ( s => s.id === pollId );

		if ( ObjWithIdLive ) {
			if ( !hasVotedValue?.[pollId] ) {
				setPollsCount ( count => count - 1 );
			}

			const updatedDataWithSoftDelete = liveData.map ( s => {
				if ( s.id === pollId ) {
					s.softDelete = true;
				}

				return s;
			} );

			setLiveData ( updatedDataWithSoftDelete );
			setTimeout ( () => {
				setLiveData ( liveData.filter ( s => s.id !== pollId ) );
			}, 3000 );

		}
		if ( ObjWithIdEnd ) {
			const updatedDataWithSoftDelete = endedData.map ( s => {
				if ( s.id === pollId ) {
					s.softDelete = true;
				}

				return s;
			} );

			setEndedData ( updatedDataWithSoftDelete );
			setTimeout ( () => {
				setEndedData ( endedData.filter ( s => s.id !== pollId ) );
			}, 3000 );

		}

	};
	const updateHasVotedFlag = ( pollsData, type ) => {
		if ( type === "API" ) {
			const newObj = {};

			pollsData?.forEach ( item => {
				newObj[item.id] = item?.hasVoted;
			} );

			updateHasVoted ( newObj );

			return;
		}

		if ( type === "CREATE" ) {
			const { id, hasVoted } = pollsData;

			updateHasVoted ( prev => ( {
				...prev,
				[id]: hasVoted
			} ) );

			return;
		}

		if ( type === "DELETE" ) {
			const key = pollsData;

			let newO = hasVotedValue;

			delete newO[key];

			updateHasVoted ( newO );

			return;
		}

	};

	return (
		<Presentation
			broadcastId      = { broadcastId }
			createPoll       = { createPoll }
			endedData        = { endedData }
			handleChangeTab  = { handleChangeTab }
			isPopOutChat     = { isPopOutChat }
			isStudioPage     = { isStudioPage }
			liveData         = { liveData }
			polls            = { polls }
			pollsConfig      = { pollsConfig }
			setPollsCount    = { setPollsCount }
			setTab           = { setTab }
			tab              = { tab }
			toggleCreatePoll = { toggleCreatePoll }
			togglePolls      = { togglePolls }
		/>
	);
};

export default component;
